import { ChapterHeader, FadeUp } from "./Motion";
import { Calendar, MapPin, ExternalLink } from "lucide-react";
import { useMemo } from "react";

export default function Schedule({ content }) {
    const today = new Date().toISOString().slice(0, 10);
    const events = content.schedule || [];
    const { upcoming, past } = useMemo(() => {
        const u = [];
        const p = [];
        events.forEach((e) => (e.date >= today ? u : p).push(e));
        u.sort((a, b) => a.date.localeCompare(b.date));
        p.sort((a, b) => b.date.localeCompare(a.date));
        return { upcoming: u, past: p };
    }, [events, today]);

    const Row = ({ e, past }) => (
        <div className="grid grid-cols-12 gap-4 items-center p-4 md:p-6 border-b border-white/10 hover:bg-white/5 transition-colors" data-testid={`event-${e.id}`}>
            <div className="col-span-2 md:col-span-1 font-mono text-brand-gold text-xs tracking-[0.15em]">
                {e.date}
            </div>
            <div className="col-span-6 md:col-span-5">
                <div className="font-mono uppercase text-base md:text-lg">{e.eventName}</div>
                <div className="chapter-num mt-1 flex items-center gap-2">
                    <MapPin size={10} /> {e.location}
                </div>
            </div>
            <div className="hidden md:block col-span-2 font-mono text-xs uppercase tracking-[0.2em] text-white/70">
                {e.type}
            </div>
            <div className="col-span-2 md:col-span-2 font-mono text-xs uppercase tracking-[0.2em] text-white/70">
                {e.weightClass} LB
            </div>
            <div className="col-span-2 md:col-span-2 text-right">
                {e.livestream ? (
                    <a
                        href={e.livestream}
                        target="_blank"
                        rel="noreferrer"
                        className="text-brand-gold font-mono text-xs uppercase tracking-[0.2em] inline-flex items-center gap-1"
                    >
                        {past ? "Result" : "Stream"} <ExternalLink size={12} />
                    </a>
                ) : (
                    <span className="font-mono text-xs text-white/30 uppercase tracking-[0.2em]">—</span>
                )}
            </div>
        </div>
    );

    return (
        <section id="schedule" className="relative py-20 md:py-32 border-t border-white/10">
            <div className="max-w-[1600px] mx-auto px-5 md:px-10">
                <ChapterHeader num="06" kicker="Competition" title="Schedule & Results" testId="chapter-schedule" />

                <FadeUp>
                    <div className="chapter-num flex items-center gap-2 mb-3">
                        <Calendar size={12} /> Upcoming
                    </div>
                    <div className="border border-white/10 bg-black">
                        {upcoming.length ? (
                            upcoming.map((e) => <Row key={e.id} e={e} />)
                        ) : (
                            <div className="p-6 font-mono text-sm text-white/50 uppercase tracking-[0.15em]">
                                Next event to be announced.
                            </div>
                        )}
                    </div>
                </FadeUp>

                <FadeUp delay={0.1}>
                    <div className="chapter-num flex items-center gap-2 mt-12 mb-3">
                        <Calendar size={12} /> Results Archive
                    </div>
                    <div className="border border-white/10 bg-black">
                        {past.length ? (
                            past.map((e) => <Row key={e.id} e={e} past />)
                        ) : (
                            <div className="p-6 font-mono text-sm text-white/50 uppercase tracking-[0.15em]">
                                Verified results will appear here as competition unfolds.
                            </div>
                        )}
                    </div>
                </FadeUp>
            </div>
        </section>
    );
}
